/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/signInUser.config.json";
import axios from "axios";

const { toHeaderCase } = require("js-convert-case");

export interface ISignInUserMethods extends IPluginMethodMap {
  signInUser(args: any): Promise<any>;
  getSignInType(args: { email: string; apiURL: string }): Promise<any>;
  getAccessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
  }): Promise<any>;
  sendOTP(args: { email: string; apiURL: string }): Promise<any>;
}

export interface HeaderProps {
  headerName: string;
  headerValue: string;
}

export interface SignInProps {
  email: string;
  password?: string;
  otp?: string;
  jwtToken?: string;
  role?: string;
  apiURL: string;
  headers: HeaderProps;
  storeId?: string;
}

export class SignInUserService implements IAgentPlugin {
  readonly methods: ISignInUserMethods;
  constructor() {
    this.methods = {
      signInUser: this.signInUser.bind(this),
      getSignInType: this.getSignInType.bind(this),
      sendOTP: this.sendOTP.bind(this),
      getAccessToken: this.getAccessToken.bind(this),
    };
  }

  async sendOTP(args: { email: string; apiURL: string }) {
    console.log(`args in sendOTP`, args);
    try {
      const res = await axios.post(`${args?.apiURL}${config?.sendOTPEP}`, {
        email: args?.email,
      });
      if (res?.data) {
        console.log(`sendOTP`, res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`sendOTP error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async getSignInType(args: { email: string; apiURL: string }) {
    try {
      const res = await axios.post(`${args?.apiURL}${config?.signInTypeEP}`, {
        email: args?.email,
      });
      if (res?.data) {
        console.log(`getSignInType`, res?.data);
        if (res?.data?.signInType === "otp") {
          await this.sendOTP(args);
        }
        return res?.data;
      }
    } catch (err: any) {
      console.log(`getSignInType error`, err?.response?.data);
      throw err?.response?.data?.error?.display_message;
    }
  }

  async getAccessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
  }) {
    try {
      const res = await axios.post(`${args?.apiURL}${config?.accessTokenEP}`, {
        jwtToken: args?.jwtToken,
        role: args?.role,
      });
      if (res?.data) {
        console.log("getAccessToken =", res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`getAccessToken error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async signInUser(args: SignInProps) {
    console.log(`args in sign in user service`, args);
    let headers: any = {};
    headers[args?.headers?.headerName] = args?.headers?.headerValue;
    console.log("login API end point", `${args.apiURL}${config?.signInEP}`);
    try {
      const res = await axios.post(`${args.apiURL}${config?.signInEP}`, {
        email: args?.email,
        password: args?.password,
        otp: args?.otp,
        jwtToken: args?.jwtToken,
        role: args?.role,
      });
      if (res?.data) {
        if (res?.data?.userRoles) {
          res.data.userRoles = this.formatRoles(res?.data?.userRoles);
        }
        return res?.data;
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw err?.response?.data?.error?.display_message;
    }
  }

  private formatRoles(roles: any[]) {
    let _res: any = [];
    for (const role of roles) {
      _res.push({
        ...(role || {}),
        title: role?.name ? toHeaderCase(role?.name) : role?.name,
      });
    }
    return _res;
  }
}
